/**
 * 正式结果网格可渲染性校验上下文。
 */
export interface MeshDataRenderValidationContext {
    /** 当前校验发生的阶段，用于错误定位。 */
    phase: string;
    /** Worker 几何对象 ID；未传入时回退到 meshData.id。 */
    objectId?: string | null;
}

/**
 * 抛出携带对象与阶段上下文的网格契约错误。
 *
 * @param context 校验上下文。
 * @param meshData 当前网格数据。
 * @param reason 不满足契约的原因。
 */
function throwMeshDataContractError(
    context: MeshDataRenderValidationContext,
    meshData: Pick<MeshData, 'id'> | null | undefined,
    reason: string,
): never {
    const objectId = context.objectId ?? meshData?.id ?? 'unknown';
    throw new Error(`[MeshDataContract] ${context.phase} 生成了不可渲染网格(objectId=${objectId}): ${reason}`);
}

/**
 * 判断结果网格是否包含至少一个可渲染三角面。
 *
 * 设计说明：
 * 1. CAD 正式结果体允许是 Puppy CAD 内核 形体、直接曲线显示网格或导入网格，但进入场景前必须统一收敛为三角面；
 * 2. 回退控制棒和 Undo/Redo 都会根据 Worker 权威状态重放并回灌 mesh，空 mesh 不能被当作成功结果；
 * 3. 该校验只检查渲染必需结构，不介入拓扑语义、离线包协议或历史命令结构。
 *
 * @param meshData Worker 返回或即将进入场景的网格数据。
 * @param context 校验上下文。
 */
export function assertRenderableMeshData(
    meshData: Pick<MeshData, 'id' | 'positions' | 'normals' | 'indices'> | null | undefined,
    context: MeshDataRenderValidationContext,
): void {
    if (!meshData) {
        throwMeshDataContractError(context, meshData, '缺少 meshData');
    }

    const positionCount = meshData.positions?.length ?? 0;
    if (positionCount < 9) {
        throwMeshDataContractError(context, meshData, `顶点坐标数量不足(${positionCount})`);
    }
    if (positionCount % 3 !== 0) {
        throwMeshDataContractError(context, meshData, `顶点坐标数量不是 3 的倍数(${positionCount})`);
    }

    const normalCount = meshData.normals?.length ?? 0;
    if (normalCount !== positionCount) {
        throwMeshDataContractError(
            context,
            meshData,
            `法向数量必须与顶点坐标数量一致(normals=${normalCount}, positions=${positionCount})`,
        );
    }

    const vertexCount = positionCount / 3;
    const indices = meshData.indices;
    if (!indices) {
        if (positionCount % 9 !== 0) {
            throwMeshDataContractError(context, meshData, `非索引网格坐标数量必须按三角面组织(${positionCount})`);
        }
        return;
    }

    if (indices.length < 3) {
        throwMeshDataContractError(context, meshData, `索引数量不足(${indices.length})`);
    }
    if (indices.length % 3 !== 0) {
        throwMeshDataContractError(context, meshData, `索引数量不是 3 的倍数(${indices.length})`);
    }

    for (let indexOffset = 0; indexOffset < indices.length; indexOffset += 1) {
        const vertexIndex = indices[indexOffset];
        if (!Number.isInteger(vertexIndex) || vertexIndex < 0 || vertexIndex >= vertexCount) {
            throwMeshDataContractError(
                context,
                meshData,
                `索引越界(indexOffset=${indexOffset}, vertexIndex=${vertexIndex}, vertexCount=${vertexCount})`,
            );
        }
    }
}
